import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { qrApi } from '@verlo/shared/services/api';
import { Button } from '@verlo/shared/components/ui/button';
import { Badge } from '@verlo/shared/components/ui/badge';
import { Input } from '@verlo/shared/components/ui/input';
import { Textarea } from '@verlo/shared/components/ui/textarea';
import { toast } from 'sonner';
import ModifierSheet, { ticketWords } from '@verlo/shared/components/ModifierSheet';
import {
  ShoppingCart, Plus, Minus, X, ChevronRight, Check, Clock,
  UtensilsCrossed, Users, ArrowLeft, Mail,
} from 'lucide-react';

const QROrderConfirmation = ({ orderResult, onOrderMore }) => {
  const [receiptEmail, setReceiptEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  // S4-8: the order was *sent* to the site's node as a command; it flips to *confirmed* once the
  // node applies it (§3.4). Poll a few times so the guest sees the kitchen pick it up.
  const [orderStatus, setOrderStatus] = useState(orderResult.status || 'sent');

  useEffect(() => {
    if (orderStatus === 'confirmed' || orderStatus === 'failed') return undefined;
    let tries = 0;
    const timer = setInterval(async () => {
      tries += 1;
      try {
        const res = await qrApi.getOrderStatus(orderResult.id);
        if (res.data?.status) setOrderStatus(res.data.status);
      } catch {
        // transient — keep polling
      }
      if (tries >= 8) clearInterval(timer);
    }, 4000);
    return () => clearInterval(timer);
  }, [orderResult.id, orderStatus]);

  const handleSendReceipt = async () => {
    if (!receiptEmail || !receiptEmail.includes('@')) {
      toast.error('Enter a valid email');
      return;
    }
    setSending(true);
    try {
      const API_BASE = import.meta.env.VITE_BACKEND_URL;
      const res = await fetch(`${API_BASE}/api/receipts/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order_id: orderResult.id, email: receiptEmail }),
      });
      const data = await res.json();
      // S4-7: the Node queues the receipt for the Cloud to send, so "queued" is success.
      if (data.status === 'queued' || data.status === 'sent' || data.status === 'preview_only') {
        toast.success('Receipt on its way!');
        setSent(true);
      } else {
        toast.error('Could not send receipt');
      }
    } catch {
      toast.error("Couldn't send receipt");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface-2 flex items-center justify-center p-6" data-testid="qr-order-confirmation">
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-sm w-full text-center">
        <div className="w-16 h-16 bg-signal-ok-tint rounded-full flex items-center justify-center mx-auto mb-4">
          <Check size={32} className="text-signal-ok" />
        </div>
        <h2 className="text-xl font-bold text-ink mb-2">Order Placed!</h2>
        <p className="text-ink-soft text-sm mb-6">
          Your order #{orderResult.id} has been sent to the kitchen.
        </p>
        <div className="bg-surface-2 rounded-lg p-4 mb-6 text-left">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-ink-soft">Table</span>
            <span className="font-medium">{orderResult.table_name}</span>
          </div>
          <div className="flex justify-between text-sm mb-2">
            <span className="text-ink-soft">Items</span>
            <span className="font-medium">{orderResult.items?.length}</span>
          </div>
          <div className="flex justify-between text-sm font-bold border-t pt-2 mt-2">
            <span>Total</span>
            <span>£{orderResult.total?.toFixed(2)}</span>
          </div>
        </div>

        {/* Email receipt */}
        {!sent ? (
          <div className="flex gap-2 mb-4">
            <Input
              type="email"
              placeholder="Email for receipt"
              value={receiptEmail}
              onChange={(e) => setReceiptEmail(e.target.value)}
              className="flex-1 text-sm"
              data-testid="qr-receipt-email-input"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={handleSendReceipt}
              disabled={sending}
              data-testid="qr-send-receipt-btn"
            >
              <Mail size={14} className="mr-1" />
              {sending ? '...' : 'Send'}
            </Button>
          </div>
        ) : (
          <p className="text-sm text-signal-ok mb-4">Receipt sent to {receiptEmail}</p>
        )}

        {orderStatus === 'confirmed' ? (
          <div className="flex items-center justify-center gap-2 text-sm text-signal-ok mb-4" data-testid="qr-order-confirmed">
            <Check size={14} />
            <span>Confirmed by the kitchen</span>
          </div>
        ) : orderStatus === 'failed' ? (
          <div className="flex items-center justify-center gap-2 text-sm text-signal-stop mb-4">
            <X size={14} />
            <span>Couldn't reach the kitchen. Please order at the bar.</span>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-2 text-sm text-signal-warn mb-4">
            <Clock size={14} />
            <span>Sent to the kitchen...</span>
          </div>
        )}
        <Button
          className="w-full bg-verlo hover:bg-verlo-hover"
          onClick={onOrderMore}
          data-testid="order-again-btn"
        >
          Order More
        </Button>
      </div>
    </div>
  );
};

const QROrderPage = () => {
  const { tableId } = useParams();
  const [table, setTable] = useState(null);
  const [menuData, setMenuData] = useState({});
  const [activeCategory, setActiveCategory] = useState('all');
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [orderNotes, setOrderNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [orderResult, setOrderResult] = useState(null);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [tableRes, menuRes] = await Promise.all([
        qrApi.getTableInfo(tableId),
        qrApi.getMenu(),
      ]);
      setTable(tableRes.data);
      setMenuData(menuRes.data.categories || {});
    } catch {
      setError('Unable to load menu. Please scan the QR code again.');
    } finally {
      setLoading(false);
    }
  }, [tableId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const categories = Object.keys(menuData);

  const displayItems = activeCategory === 'all'
    ? Object.values(menuData).flat()
    : menuData[activeCategory] || [];

  // The modifier sheet for a guest (modifiers plan): the item's groups only — no search, no
  // free text — priced by the Cloud when the order is placed.
  const [sheetItem, setSheetItem] = useState(null);
  const guestConfig = { free_text_pricing: false, free_text_cap: 0, free_text_step: 0.5 };
  const addWithModifiers = (item, mods, modifierTotal, unit) => {
    const words = ticketWords(mods);
    setCart((prev) => [...prev, {
      cartKey: `${item.id}-${Date.now()}`, menu_item_id: item.id, name: item.name, size: null,
      price: unit, quantity: 1, modifiers: mods, notes: '', words,
    }]);
    toast.success(`Added ${item.name}${words.length ? ` · ${words.join(', ')}` : ''}`, { duration: 1500 });
  };

  const addToCart = (item, size = null) => {
    if (!size && (item.always_ask || item.modifier_groups?.length) && !item.has_sizes) { setSheetItem(item); return; }
    const cartKey = `${item.id}-${size || 'default'}`;
    const price = size
      ? item.size_options?.find(s => s.name === size)?.price || item.price
      : item.price;

    setCart(prev => {
      const idx = prev.findIndex(c => c.cartKey === cartKey);
      if (idx >= 0) {
        const updated = [...prev];
        updated[idx] = { ...updated[idx], quantity: updated[idx].quantity + 1 };
        return updated;
      }
      return [...prev, {
        cartKey,
        menu_item_id: item.id,
        name: item.name,
        size,
        price,
        quantity: 1,
        modifiers: [],
        notes: '',
      }];
    });
    toast.success(`Added ${item.name}${size ? ` (${size})` : ''}`, { duration: 1500 });
  };

  const updateCartQuantity = (cartKey, delta) => {
    setCart(prev => {
      return prev.map(item => {
        if (item.cartKey !== cartKey) return item;
        const newQty = item.quantity + delta;
        if (newQty <= 0) return null;
        return { ...item, quantity: newQty };
      }).filter(Boolean);
    });
  };

  const removeFromCart = (cartKey) => {
    setCart(prev => prev.filter(c => c.cartKey !== cartKey));
  };

  const cartTotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  const handleSubmitOrder = async () => {
    if (cart.length === 0) {
      toast.error('Your basket is empty');
      return;
    }
    setSubmitting(true);
    try {
      const res = await qrApi.placeOrder({
        table_id: tableId,
        customer_name: customerName || 'Guest',
        items: cart.map(c => ({
          menu_item_id: c.menu_item_id,
          name: c.name,
          quantity: c.quantity,
          size: c.size,
          modifiers: c.modifiers,
          price: c.price,
          notes: c.notes,
        })),
        notes: orderNotes,
      });
      setOrderResult(res.data);
      setCart([]);
      setCartOpen(false);
    } catch {
      toast.error("Couldn't place order. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-2 flex items-center justify-center">
        <div className="text-center">
          <UtensilsCrossed size={40} className="mx-auto mb-4 text-verlo animate-pulse" />
          <p className="text-ink-soft">Loading menu...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-surface-2 flex items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <X size={40} className="mx-auto mb-4 text-signal-stop" />
          <p className="text-ink-soft font-medium mb-2">Oops!</p>
          <p className="text-ink-soft text-sm">{error}</p>
        </div>
      </div>
    );
  }

  // Order confirmation screen
  if (orderResult) {
    return (
      <QROrderConfirmation 
        orderResult={orderResult} 
        onOrderMore={() => setOrderResult(null)} 
      />
    );
  }

  return (
    <div className="min-h-screen bg-surface-2 pb-24" data-testid="qr-order-page">
      {/* Header */}
      <div className="bg-verlo text-white px-4 py-4 sticky top-0 z-30">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold">{table?.restaurant_name || 'Order at your table'}</h1>
            <p className="text-white/70 text-xs flex items-center gap-1">
              <Users size={12} /> {table?.name} · Scan to order
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="border-white/30 text-white hover:bg-white/10 relative"
            onClick={() => setCartOpen(true)}
            data-testid="open-cart-btn"
          >
            <ShoppingCart size={16} />
            {cartCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-signal-stop-line text-white text-[11px] w-5 h-5 rounded-full flex items-center justify-center font-bold">
                {cartCount}
              </span>
            )}
          </Button>
        </div>
      </div>

      {/* Categories */}
      <div className="sticky top-[60px] z-20 bg-white border-b">
        <div className="max-w-lg mx-auto overflow-x-auto flex gap-1 p-2">
          <button
            className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
              activeCategory === 'all' ? 'bg-verlo text-white' : 'bg-paper text-ink-soft hover:bg-hairline'
            }`}
            onClick={() => setActiveCategory('all')}
          >
            All
          </button>
          {categories.map(cat => (
            <button
              key={cat}
              className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                activeCategory === cat ? 'bg-verlo text-white' : 'bg-paper text-ink-soft hover:bg-hairline'
              }`}
              onClick={() => setActiveCategory(cat)}
              data-testid={`qr-category-${cat}`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Menu Items */}
      <div className="max-w-lg mx-auto p-4 space-y-3">
        {displayItems.map(item => (
          <div
            key={item.id}
            className={`rounded-lg border border-hairline p-4 ${item.available_now === false ? 'bg-surface-2' : 'bg-white'}`}
            data-testid={`qr-menu-item-${item.id}`}
            data-off={item.available_now === false ? '1' : undefined}
          >
            <div className="flex justify-between items-start">
              <div className="flex-1 mr-3">
                <h3 className={`font-semibold text-sm ${item.available_now === false ? 'text-ink-faint' : 'text-ink'}`}>{item.name}</h3>
                {item.description && (
                  <p className="text-xs text-ink-soft mt-0.5 line-clamp-2">{item.description}</p>
                )}
                {/* A dish that is off stays on the menu with its hours (hours plan): "is
                    the lunch menu still on?" is the question a QR menu exists to answer
                    without anybody having to ask the floor. */}
                {item.available_now === false && (
                  <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.05em] text-signal-warn" data-testid={`qr-hours-${item.id}`}>
                    {item.hours || 'not on right now'}
                  </p>
                )}
              </div>
              <div className="text-right shrink-0">
                {item.available_now === false ? (
                  <span className="font-mono text-sm font-semibold text-ink-faint">£{item.price?.toFixed(2)}</span>
                ) : item.has_sizes && item.size_options?.length > 0 ? (
                  <div className="space-y-1">
                    {item.size_options.map(s => (
                      <Button
                        key={s.name}
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs w-full justify-between gap-2"
                        onClick={() => addToCart(item, s.name)}
                        data-testid={`add-${item.id}-${s.name}`}
                      >
                        <span>{s.name}</span>
                        <span className="font-semibold font-mono">£{s.price?.toFixed(2)}</span>
                      </Button>
                    ))}
                  </div>
                ) : (
                  <Button
                    size="sm"
                    className="bg-verlo hover:bg-verlo-hover h-8 text-xs"
                    onClick={() => addToCart(item)}
                    data-testid={`add-${item.id}`}
                  >
                    £{item.price?.toFixed(2)} <Plus size={12} className="ml-1" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
        {displayItems.length === 0 && (
          <p className="text-center text-ink-faint py-8 text-sm">No items in this category</p>
        )}
      </div>

      {/* Fixed bottom cart bar */}
      {cartCount > 0 && !cartOpen && (
        <div className="fixed bottom-0 left-0 right-0 bg-verlo text-white p-3 z-30 shadow-lg">
          <div className="max-w-lg mx-auto flex items-center justify-between">
            <div>
              <span className="font-bold text-sm">{cartCount} item{cartCount > 1 ? 's' : ''}</span>
              <span className="text-white/70 text-sm ml-2 font-mono">£{cartTotal.toFixed(2)}</span>
            </div>
            <Button
              className="bg-white text-verlo hover:bg-white/90 font-bold text-sm"
              onClick={() => setCartOpen(true)}
              data-testid="view-basket-btn"
            >
              View Basket <ChevronRight size={16} className="ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Cart overlay */}
      {cartOpen && (
        <div className="fixed inset-0 z-40 bg-black/50" onClick={() => setCartOpen(false)}>
          <div
            className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="cart-panel"
          >
            <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
              <h2 className="font-bold text-lg">Your Basket</h2>
              <Button variant="ghost" size="sm" onClick={() => setCartOpen(false)}>
                <X size={18} />
              </Button>
            </div>
            <div className="p-4 space-y-3">
              {cart.map(item => (
                <div key={item.cartKey} className="flex items-center justify-between py-2 border-b">
                  <div className="flex-1">
                    <p className="text-sm font-medium">{item.name}</p>
                    {item.size && <p className="text-xs text-ink-soft">{item.size}</p>}
                    {item.words?.length > 0 && <p className="text-xs text-ink-soft">{item.words.join(' · ')}</p>}
                    <p className="text-xs text-ink-soft font-medium font-mono">£{(item.price * item.quantity).toFixed(2)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      className="w-7 h-7 rounded-full border flex items-center justify-center text-ink-soft hover:bg-hover-tint"
                      onClick={() => updateCartQuantity(item.cartKey, -1)}
                      data-testid={`cart-minus-${item.cartKey}`}
                    >
                      <Minus size={12} />
                    </button>
                    <span className="w-6 text-center text-sm font-bold">{item.quantity}</span>
                    <button
                      className="w-7 h-7 rounded-full border flex items-center justify-center text-ink-soft hover:bg-hover-tint"
                      onClick={() => updateCartQuantity(item.cartKey, 1)}
                      data-testid={`cart-plus-${item.cartKey}`}
                    >
                      <Plus size={12} />
                    </button>
                    <button
                      className="w-7 h-7 rounded-full flex items-center justify-center text-signal-stop hover:bg-signal-stop-tint"
                      onClick={() => removeFromCart(item.cartKey)}
                    >
                      <X size={14} />
                    </button>
                  </div>
                </div>
              ))}
              {cart.length === 0 && (
                <p className="text-center text-ink-faint py-6 text-sm">Your basket is empty</p>
              )}
            </div>
            {cart.length > 0 && (
              <div className="sticky bottom-0 bg-white border-t p-4 space-y-3">
                <Input
                  placeholder="Your name (optional)"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  className="text-sm"
                  data-testid="qr-customer-name"
                />
                <Textarea
                  placeholder="Any special requests?"
                  value={orderNotes}
                  onChange={(e) => setOrderNotes(e.target.value)}
                  className="text-sm min-h-[50px]"
                />
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold text-lg">Total</span>
                  <span className="font-bold text-lg font-mono">£{cartTotal.toFixed(2)}</span>
                </div>
                <Button
                  className="w-full bg-verlo hover:bg-verlo-hover h-12 text-base font-bold"
                  onClick={handleSubmitOrder}
                  disabled={submitting}
                  data-testid="submit-qr-order-btn"
                >
                  {submitting ? 'Sending to kitchen...' : `Place Order · £${cartTotal.toFixed(2)}`}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
      <ModifierSheet
        open={!!sheetItem}
        onOpenChange={(v) => { if (!v) setSheetItem(null); }}
        item={sheetItem}
        library={[]}
        config={guestConfig}
        allowAlso={false}
        formatCurrency={(v) => `£${Number(v || 0).toFixed(2)}`}
        onConfirm={addWithModifiers}
      />
    </div>
  );
};

export default QROrderPage;
